import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { MapContainer, Marker, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import DarkBasemap from '../maps/DarkBasemap';

// Fix for default marker icons in Leaflet with Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Region mapping - using verified KGMIDs that Google Travel Explore recognizes
// These are the official Freebase IDs for continents from Wikidata
export const EXPLORE_AREA_IDS = {
  world: '/m/01',
  asia: '/m/0j0k',
  africa: '/m/0dg3n1',
  europe: '/m/02j9z',
  north_america: '/m/059g4',
  south_america: '/m/06n3y',
  oceania: '/m/05nrg',
};

// Keep REGION_KGMIDS for backward compatibility with existing code
const REGION_KGMIDS = {
  europe: EXPLORE_AREA_IDS.europe,
  asia: EXPLORE_AREA_IDS.asia,
  africa: EXPLORE_AREA_IDS.africa,
  south_america: EXPLORE_AREA_IDS.south_america,
  north_america: EXPLORE_AREA_IDS.north_america,
  oceania: EXPLORE_AREA_IDS.oceania,
  middle_east: '/m/0b90f', // Middle East - keeping as is (may need verification)
};

function detectRegionFromBounds(bounds) {
  const center = bounds.getCenter();
  const lat = center.lat;
  const lng = center.lng;
  
  // Get bounds to check if a significant portion of the view is in a region
  const east = bounds.getEast();
  const west = bounds.getWest();
  
  // Check which region the center point is in, with priority order
  // (check more specific regions first, then broader ones)
  
  // Middle East (check first as it overlaps with Asia/Europe)
  if (lat >= 12 && lat <= 42 && lng >= 25 && lng <= 65) {
    return REGION_KGMIDS.middle_east;
  }
  
  // Europe
  if (lat >= 35 && lat <= 72 && lng >= -25 && lng <= 40) {
    return REGION_KGMIDS.europe;
  }
  
  // Asia: Check if center OR significant portion of view is in Asia
  // Asia spans roughly: lat -10 to 55, lng 60 to 180
  // Check center point first
  if (lat >= -10 && lat <= 55 && lng >= 60 && lng <= 180) {
    return REGION_KGMIDS.asia;
  }
  
  // Also check if a significant portion of the visible bounds is in Asia
  // This helps when panning across regions
  const boundsCenterLng = (east + west) / 2;
  if (lat >= -10 && lat <= 55 && boundsCenterLng >= 60 && boundsCenterLng <= 180) {
    return REGION_KGMIDS.asia;
  }
  
  // Check if east edge is in Asia (for when scrolling from Europe/Middle East)
  // Also check if west edge crosses into Asia (for when scrolling from Middle East)
  if (lat >= -10 && lat <= 55) {
    if ((east >= 60 && east <= 180) || (west >= 60 && west <= 180)) {
      return REGION_KGMIDS.asia;
    }
  }
  
  // Oceania (Australia, New Zealand, Pacific Islands)
  if (lat >= -50 && lat <= 0 && lng >= 110 && lng <= 180) {
    return REGION_KGMIDS.oceania;
  }
  
  // Africa
  if (lat >= -35 && lat <= 37 && lng >= -20 && lng <= 55) {
    return REGION_KGMIDS.africa;
  }
  
  // South America
  if (lat >= -55 && lat <= 12 && lng >= -80 && lng <= -35) {
    return REGION_KGMIDS.south_america;
  }
  
  // North America (default/fallback for US, Canada, Mexico)
  if (lat >= 10 && lat <= 75 && lng >= -180 && lng <= -50) {
    return REGION_KGMIDS.north_america;
  }
  
  return null;
}

// Simple handler for region changes
function MapRegionHandler({ onRegionChange, origin, onBoundsChange }) {
  const map = useMap();
  const lastRegionRef = useRef(null);
  const timeoutRef = useRef(null);
  const isSearchingRef = useRef(false);
  const isUserInteractingRef = useRef(false);

  useEffect(() => {
    if (!onRegionChange || !origin) {
      // Still handle bounds updates even without region change
      if (onBoundsChange) {
        const updateBounds = () => {
          try {
            const bounds = map.getBounds();
            onBoundsChange({
              north: bounds.getNorth(),
              south: bounds.getSouth(),
              east: bounds.getEast(),
              west: bounds.getWest()
            });
          } catch (e) {
            // Map might not be ready
          }
        };
        map.on('moveend', updateBounds);
        map.on('zoomend', updateBounds);
        updateBounds();
        return () => {
          map.off('moveend', updateBounds);
          map.off('zoomend', updateBounds);
        };
      }
      return;
    }

    const handleInteractionStart = () => {
      isUserInteractingRef.current = true;
    };

    const handleMove = () => {
      // Always update bounds, but only trigger region search if user is interacting
      if (onBoundsChange) {
        try {
          const bounds = map.getBounds();
          onBoundsChange({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          });
        } catch (e) {
          // Map might not be ready
        }
      }
      
      // Only trigger region search if user is actively interacting
      if (isSearchingRef.current || !isUserInteractingRef.current) return;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      timeoutRef.current = setTimeout(() => {
        try {
          const bounds = map.getBounds();
          const regionKgmid = detectRegionFromBounds(bounds);

          if (regionKgmid && regionKgmid !== lastRegionRef.current) {
            lastRegionRef.current = regionKgmid;
            isSearchingRef.current = true;
            onRegionChange(regionKgmid, bounds).finally(() => {
              setTimeout(() => {
                isSearchingRef.current = false;
              }, 1000);
            });
          }
        } catch (e) {
          console.error('[MapRegionHandler] Error:', e);
          isSearchingRef.current = false;
        } finally {
          isUserInteractingRef.current = false;
        }
      }, 800);
    };

    map.on('dragstart', handleInteractionStart);
    map.on('zoomstart', handleInteractionStart);
    map.on('moveend', handleMove);
    map.on('zoomend', handleMove);
    
    // Initial bounds
    if (onBoundsChange) {
      try {
        const bounds = map.getBounds();
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      } catch (e) {
        // Map might not be ready yet
      }
    }

    return () => {
      map.off('dragstart', handleInteractionStart);
      map.off('zoomstart', handleInteractionStart);
      map.off('moveend', handleMove);
      map.off('zoomend', handleMove);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [map, onRegionChange, origin, onBoundsChange]);

  return null;
}

// Create blue circular marker — royal mid anchor
function createBlueMarkerIcon() {
  return L.divIcon({
    className: 'custom-blue-marker',
    html: `
      <div style="
        position: relative;
        width: 14px;
        height: 14px;
        background: #0F1952;
        border-radius: 50%;
        border: 2px solid rgba(96, 130, 210, 0.85);
        box-shadow: 0 2px 10px rgba(10, 18, 51, 0.65);
        cursor: pointer;
      ">
        <div style="
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        "></div>
      </div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// Selected destination — brand accent ring
function createRedPinIcon() {
  return L.divIcon({
    className: 'custom-red-pin',
    html: `
      <div style="
        position: relative;
        width: 18px;
        height: 18px;
      ">
        <div style="
          background: #1e3a8a;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border: 2px solid rgba(147, 170, 255, 0.95);
          box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.35), 0 2px 10px rgba(10, 18, 51, 0.65);
        "></div>
      </div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

export default function ExploreMap({ 
  destinations, 
  origin, 
  center, 
  zoom, 
  onDestinationClick, 
  selectedDestination, 
  onRegionChange, 
  searchParams, 
  skipAutoCenter = false,
  onBoundsChange 
}) {
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;
  
  // Lock initial center/zoom to prevent map resets when props change
  const initialCenterRef = useRef(center ? [center.lat, center.lng] : defaultCenter);
  const initialZoomRef = useRef(zoom || defaultZoom);

  // Filter valid destinations with coordinates - limit to prevent crashes
  const validDestinations = useMemo(() => {
    try {
      const valid = destinations.filter(d => {
        if (!d || !d.gps_coordinates?.latitude || !d.gps_coordinates?.longitude) return false;
        const lat = parseFloat(d.gps_coordinates.latitude);
        const lng = parseFloat(d.gps_coordinates.longitude);
        if (isNaN(lat) || isNaN(lng)) return false;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
        return true;
      });
      // Limit to 500 markers to prevent performance issues
      // This should be enough for most use cases while maintaining good performance
      return valid.slice(0, 500);
    } catch (error) {
      console.error('Error filtering destinations:', error);
      return [];
    }
  }, [destinations]);

  // Use locked initial values - never update from props after mount
  const mapCenter = initialCenterRef.current;
  const mapZoom = initialZoomRef.current;

  return (
    <div className="w-full h-full min-h-[400px] rounded-2xl overflow-hidden relative">
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        scrollWheelZoom={true}
        className="dark-explore-map"
        key="explore-map-stable"
      >
        <DarkBasemap />
        
        {onRegionChange && origin && (
          <MapRegionHandler 
            onRegionChange={onRegionChange} 
            origin={origin}
            onBoundsChange={onBoundsChange}
          />
        )}

        {validDestinations.map((destination) => {
          try {
            if (!destination || !destination.gps_coordinates) return null;
            
            // Double-check coordinates are valid before rendering
            const lat = parseFloat(destination.gps_coordinates.latitude);
            const lng = parseFloat(destination.gps_coordinates.longitude);
            if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
              return null;
            }
            
            const isSelected = selectedDestination?.destination_id === destination.destination_id;
            const markerKey = destination.destination_id || `${lat}-${lng}-${destination.name || 'dest'}`;

          return (
            <Marker
              key={markerKey}
              position={[lat, lng]}
              icon={isSelected ? createRedPinIcon() : createBlueMarkerIcon()}
              eventHandlers={{
                click: () => {
                  if (onDestinationClick) {
                    onDestinationClick(destination);
                  }
                },
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -8]}
                opacity={1}
                permanent
                className="destination-overlay-tooltip"
                interactive={true}
              >
                <div 
                  className="destination-overlay-content"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onDestinationClick) {
                      onDestinationClick(destination);
                    }
                  }}
                  style={{ pointerEvents: 'auto' }}
                >
                  <div className="destination-name">{destination.name}</div>
                  {destination.flight_price && !isNaN(parseFloat(destination.flight_price)) ? (
                    <div className="destination-price">${Math.round(parseFloat(destination.flight_price) * 0.2)} today</div>
                  ) : (
                    <div className="destination-price">N/A</div>
                  )}
                  {destination.flight_duration && (
                    <div className="destination-duration">
                      {Math.floor(destination.flight_duration / 60)}h {destination.flight_duration % 60}m
                    </div>
                  )}
                </div>
              </Tooltip>
            </Marker>
          );
          } catch (error) {
            console.error('Error rendering marker:', error, destination);
            return null;
          }
        })}
      </MapContainer>

      <style>{`
        .dark-explore-map .leaflet-container {
          background: #0A1233;
        }
        
        /* The navy tint the old raster tiles had, now on the MapLibre basemap. */
        .dark-explore-map .leaflet-gl-layer {
          filter: brightness(0.78) contrast(1.08) saturate(0.65) hue-rotate(205deg);
        }
        
        .destination-overlay-tooltip {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          margin: 0 !important;
          pointer-events: auto !important;
        }
        
        .destination-overlay-tooltip .leaflet-tooltip-content-wrapper {
          pointer-events: auto !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        
        .destination-overlay-tooltip .leaflet-tooltip-content {
          pointer-events: auto !important;
        }
        
        .destination-overlay-content {
          background: linear-gradient(to bottom, rgba(15, 25, 82, 0.96), rgba(10, 18, 51, 0.96));
          border-radius: 10px;
          padding: 7px 11px;
          min-width: 88px;
          box-shadow: 0 4px 16px rgba(10, 18, 51, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.07);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(72, 92, 150, 0.5);
          cursor: pointer;
          transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
        }
        
        .destination-overlay-content:hover {
          border-color: rgba(110, 140, 220, 0.65);
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(10, 18, 51, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.09);
        }
        
        .destination-name {
          color: #ffffff;
          font-size: 11px;
          font-weight: 600;
          margin-bottom: 2px;
          letter-spacing: 0.01em;
        }
        
        .destination-price {
          color: rgba(186, 200, 255, 0.95);
          font-size: 10px;
          font-weight: 500;
        }
        
        .destination-duration {
          color: rgba(255, 255, 255, 0.55);
          font-size: 9px;
          margin-top: 2px;
        }
        
        .dark-explore-map .leaflet-control-zoom {
          border: 1px solid rgba(72, 92, 150, 0.45);
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 4px 14px rgba(10, 18, 51, 0.45);
        }
        
        .dark-explore-map .leaflet-control-zoom a {
          background: rgba(10, 18, 51, 0.92);
          color: rgba(220, 228, 255, 0.9);
          border-bottom: 1px solid rgba(72, 92, 150, 0.35);
          width: 32px;
          height: 32px;
          line-height: 32px;
        }
        
        .dark-explore-map .leaflet-control-zoom a:last-child {
          border-bottom: none;
        }
        
        .dark-explore-map .leaflet-control-zoom a:hover {
          background: rgba(15, 25, 82, 0.95);
          color: #ffffff;
        }
        
        .dark-explore-map .leaflet-control-attribution {
          background: rgba(10, 18, 51, 0.85);
          border: 1px solid rgba(72, 92, 150, 0.35);
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.55);
          font-size: 10px;
          padding: 2px 6px;
        }
        
        .dark-explore-map .leaflet-control-attribution a {
          color: rgba(186, 200, 255, 0.85);
        }
      `}</style>
    </div>
  );
}
